import React from 'react';

import { ContentRenderingError, HeadingContextProvider } from '@af/design-system-docs-ui';
import { Inline } from '@atlaskit/primitives';
import { ContextualAnalyticsData, FireScreenAnalytics, SCREEN } from '@atlassian/analytics-bridge';

import ContentRenderer from '../components/content-renderer';
import { normalizeSourceName } from '../utils/analytics';
import { EMPTY_TITLE_PLACEHOLDER } from '../utils/constants';
import titleify from '../utils/titleify';

import Page from './page';

type PageContext = {
	/**
	 * This is the guideline data from Contentful, coming from `gatsby-node.js`
	 */
	guideline: Queries.ContentfulGuideline;
	/**
	 * The slug of the guideline for backwards-compatibility, should match `context.guideline.slug`
	 */
	slug: string;
};

/**
 * __Guideline page__
 *
 * A guideline page.
 */
const GuidelinePage: React.ComponentType<{ pageContext: PageContext }> = ({
	pageContext: { guideline },
}) => {
	if (guideline) {
		const title = guideline.title || EMPTY_TITLE_PLACEHOLDER;
		const parentPage = guideline?.contentfulparent
			? titleify(guideline.contentfulparent.slug || '')
			: title;

		const parentPageTitle = parentPage === title ? '' : parentPage;

		const sourceName = normalizeSourceName(guideline.category || '', guideline.title || '');
		const categoryName = normalizeSourceName(guideline.category || '');

		return (
			<ContextualAnalyticsData
				sourceType={SCREEN}
				sourceName="content"
				attributes={{ name: sourceName, context: categoryName }}
			>
				<HeadingContextProvider>
					<Page
						parentPageTitle={parentPageTitle}
						title={title}
						description={guideline.description?.description || ''}
						guidelineCategory={guideline.category || ''}
					>
						<Inline alignInline="center">
							{guideline?.bodyText?.raw && (
								<ContentRenderer richText={guideline.bodyText}>
									<FireScreenAnalytics />
								</ContentRenderer>
							)}
						</Inline>
					</Page>
				</HeadingContextProvider>
			</ContextualAnalyticsData>
		);
	}

	return <ContentRenderingError />;
};

export default GuidelinePage;
